import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAgentDock } from "../../context/AgentDockContext";

const NAV_ITEMS = [
  { label: "Work", href: "/", key: "work" },
  { label: "About", href: "/about", key: "about" },
  { label: "Resume", href: "/resume", key: "resume" },
  { label: "Digital Young", href: "/agent", key: "agent", openDock: true },
];

const FOOTER_LINKS = [
  { label: "Writing", href: "/#writing" },
  { label: "Resume", href: "/resume" },
  { label: "Life notes", href: "/RandomLifeNotes" },
  { label: "Leave feedback", href: "/feedback" },
  { label: "Accessibility", href: "/AccessibilityPage" },
];

function isNavActive(item, pathname) {
  const path = pathname.toLowerCase();
  if (item.key === "work") return path === "/" || path === "/portfolio";
  if (item.key === "about") return path === "/about";
  if (item.key === "agent") return path === "/agent";
  if (item.key === "resume") return path === "/resume";
  return path === item.href.toLowerCase().split("#")[0];
}

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openDock } = useAgentDock();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (!section) return;
    const offsetPosition = section.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  const handleWorkClick = (e) => {
    if (location.pathname === "/" || location.pathname === "/portfolio") {
      e.preventDefault();
      scrollToSection("my-works");
    } else {
      e.preventDefault();
      navigate("/#my-works");
    }
    setMobileMenuOpen(false);
  };

  const handleNavClick = (item) => (e) => {
    if (item.key === "work") {
      handleWorkClick(e);
      return;
    }
    setMobileMenuOpen(false);
  };

  const renderNavLink = (item, index, mobile = false) => {
    const isActive = isNavActive(item, location.pathname);
    const isExternalLink = item.href?.startsWith("http");

    const linkClasses = mobile
      ? `block py-3 text-lg font-medium border-b border-gray-800 ${
          isActive ? "text-white" : "text-gray-300 hover:text-white"
        }`
      : `relative focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black rounded-sm`;

    const labelClasses = mobile
      ? undefined
      : `text-sm font-medium transition-all duration-300 relative inline-block after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-white after:transition-all after:duration-300 hover:after:w-full focus-within:after:w-full ${
          isActive ? "text-white after:w-full" : "text-white/85 hover:text-white"
        }`;

    const label = mobile ? item.label : <span className={labelClasses}>{item.label}</span>;

    if (isExternalLink) {
      return (
        <a
          key={item.href || index}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClasses}
          onClick={() => setMobileMenuOpen(false)}
        >
          {label}
        </a>
      );
    }

    if (item.openDock) {
      return (
        <button
          key={item.key || item.href || index}
          type="button"
          onClick={() => {
            openDock();
            setMobileMenuOpen(false);
          }}
          className={mobile ? `${linkClasses} w-full text-left` : linkClasses}
        >
          {label}
        </button>
      );
    }

    return (
      <Link
        key={item.key || item.href || index}
        to={item.href}
        onClick={handleNavClick(item)}
        className={linkClasses}
      >
        {label}
      </Link>
    );
  };

  const renderFooterLink = (item) => (
    <Link key={item.href} to={item.href} className="text-gray-600 hover:text-gray-900 hover:underline text-sm">
      {item.label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-white text-primary font-[Helvetica, sans-serif]">
      <a
        href="#projects"
        className="sr-only focus:not-sr-only absolute top-4 left-4 bg-white text-black p-2 rounded-md shadow-md"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-50 flex justify-between items-center px-4 sm:px-8 py-4 bg-black border-b border-gray-800">
        <h1 className="text-l">
          <Link to="/" className="hover:underline text-white">
            YOUNG.DESIGN
          </Link>
        </h1>

        <nav className="hidden md:flex items-center gap-10" aria-label="Main navigation">
          {NAV_ITEMS.map((item, index) => renderNavLink(item, index))}
        </nav>

        <button
          type="button"
          className="md:hidden text-white p-2 -mr-2 rounded-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-nav"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {mobileMenuOpen && (
        <nav
          id="mobile-nav"
          className="md:hidden fixed inset-0 top-[65px] z-40 bg-black px-4 sm:px-8 py-6 overflow-y-auto"
          aria-label="Mobile navigation"
        >
          <div className="flex flex-col">{NAV_ITEMS.map((item, index) => renderNavLink(item, index, true))}</div>
          <p className="mt-8 mb-3 text-xs uppercase tracking-wider text-gray-500">More</p>
          <div className="flex flex-col gap-1">
            {FOOTER_LINKS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="py-2 text-base text-gray-400 hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </nav>
      )}

      <main>{children}</main>

      <footer className="py-8 px-6 sm:px-8 bg-white border-t text-center text-gray-800">
        <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 mb-4">
          {FOOTER_LINKS.map(renderFooterLink)}
        </div>
        <p className="text-sm text-gray-500">&copy; 2025 Young. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Layout;
